<template>
  <div>
    <div class="d-flex justify-content-between">
      <label v-if="label" class="form-label text-muted">
        {{ label }} <i v-if="required" class="text-danger">*</i>
      </label>
      <slot name="labelContentEnd"></slot>
    </div>
    <div
      v-if="uploading && !hiddenInput"
      class="border border-start-0 border-end-0 d-flex align-items-center py-3"
    >
      <i class="fas fa-file-image text-primary display-3"></i>
      <div class="w-100 mx-4">
        <div class="d-flex justify-content-between">
          <strong> {{ fileName }} </strong>
          <strong class="text-primary">{{ percentage }}%</strong>
        </div>
        <div class="progress">
          <div
            class="progress-bar"
            role="progressbar"
            :style="`width: ${percentage}%`"
            aria-valuenow="25"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
      <i class="fas fa-times display-4 text-light" @click="cancelUpload"></i>
    </div>
    <template v-else>
      <div class="" v-if="hiddenInput">
        <input
          hidden
          :id="inputId"
          ref="fileInputRef"
          type="file"
          :name="input_name"
          :accept="accept"
          @change="setImage"
          :style="{ width: customFileInputWidth + 'px' }"
        />
      </div>
      <div v-else class="form-control-custom mb-3 text-muted">
        <input
          :id="inputId"
          ref="fileInputRef"
          type="file"
          :name="input_name"
          :accept="accept"
          @change="setImage"
          :style="{ width: customFileInputWidth + 'px' }"
        />
        <img
          :src="cropImg"
          v-if="cropImg"
          width="200"
          :height="aspectRatio == 1 ? 200 : 300"
          class="rounded"
          style="object-fit: contain"
        />
        <div class="text" v-else>
          <img
            v-if="customIcon"
            :src="customIcon"
            alt=""
            height="100"
            class="mb-3"
          />
          <i v-else class="fas fa-file-image display-4 mb-3"></i>
          <h2 class="h2 mb-1">
            <span> Clique ou arraste uma imagem </span>
          </h2>
          <small>Tamanho máximo: {{ maxImgSize }}mb</small>
        </div>
      </div>
    </template>
    <ModalComponent
      title="Crop Imagem"
      :id="modal_id"
      v-on:modalClosed="cropClosed"
      :backdrop="backdrop"
    >
      <template v-slot:body>
        <vue-cropper
          :modal="false"
          :movable="false"
          :zoomable="false"
          ref="cropperRef"
          :src="imgSrc"
          alt="Crop Imagem"
          :aspect-ratio="aspectRatio"
          :minContainerWidth="550"
          :minContainerHeight="350"
        >
        </vue-cropper>
      </template>
      <template v-slot:footer>
        <button
          type="button"
          @click="cropImage"
          class="btn btn-secondary"
          :id="button_id"
        >
          Concluir
        </button>
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import VueCropper from "vue-cropperjs";
// @ts-ignore
import "cropperjs/dist/cropper.css";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { dataURLtoFile, toggleModal } from "@/utils/Helper";

const props = withDefaults(
  defineProps<{
    accept?: string;
    customFileInputWidth?: number;
    aspectRatio?: number;
    inputId?: string;
    hiddenInput?: boolean;
    modalId?: string;
    maxImgSize?: number;
    customIcon?: string | null;
    label?: string;
    required?: boolean;
    backdrop?: string;
    image?: string;
  }>(),
  {
    accept: "image/*,.pdf",
    customFileInputWidth: 100,
    aspectRatio: 1,
    inputId: () => `def-${new Date().getTime()}`,
    hiddenInput: false,
    modalId: () => `${new Date().getTime()}`,
    maxImgSize: 8,
    customIcon: null,
    label: "",
    required: false,
    backdrop: "static",
    image: "",
  },
);

const emit = defineEmits<{
  cropEnd: [result: { file: File | null; base64: string | ArrayBuffer | null }];
}>();

const cropperRef = ref<InstanceType<typeof VueCropper> | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const imgSrc = ref<string | ArrayBuffer | null>(null);
const cropImg = ref(props.image);
const fileImage = ref<File | null>(null);
const percentage = ref(0);
const uploading = ref(false);
const counter = ref<ReturnType<typeof setTimeout> | null>(null);
const originalFile = ref<File | null>(null);

const modal_id = computed(() => `image-modal-${props.modalId}`);
const fileName = computed(() =>
  originalFile.value ? `${originalFile.value.name}` : ``,
);
const input_name = computed(() => `${props.inputId}-name`);
const button_id = computed(() => `${props.inputId}_button`);

onMounted(() => {
  EventBus.$on(EventBusNames.CLEAR_IMAGE_CROPPER, () => {
    imgSrc.value = null;
    cropImg.value = props.image;
    fileImage.value = null;
  });
});

const isImageSizeValid = (value: File | null, maxSizeInMb = 8): boolean => {
  if (!value) {
    return true;
  }
  return value.size / 1024 / 1024 <= maxSizeInMb;
};

const upload = () => {
  uploading.value = true;
  let timer = Math.floor(Math.random() * 500) + 500;
  percentage.value = 100;
  timer = 1;
  counter.value = setTimeout(() => {
    setCounter();
  }, timer);
};

const setCounter = () => {
  if (percentage.value < 100) {
    const max = 100;
    const p =
      Math.floor(Math.random() * (max - percentage.value + 1)) +
      percentage.value;
    percentage.value = p >= 100 ? 100 : p;
    upload();
  } else {
    uploading.value = false;
    percentage.value = 0;
    if (counter.value) {
      clearTimeout(counter.value);
    }
    const result = { file: fileImage.value, base64: cropImg.value };
    emit("cropEnd", result);
    cropClosed();
  }
};

const cropImage = () => {
  const cropper = cropperRef.value as any;
  cropImg.value = cropper.getCroppedCanvas().toDataURL();
  fileImage.value = dataURLtoFile(
    cropper.getCroppedCanvas().toDataURL("image/jpeg"),
    "text.jpeg",
  );

  if (fileImage.value.size > props.maxImgSize * 1024 * 1024) {
    fileImage.value = dataURLtoFile(
      cropper.getCroppedCanvas().toDataURL("image/jpeg", 0.7),
      "text.jpeg",
    );
  }
  toggleModal(modal_id.value);
  upload();
};

const setImage = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    originalFile.value = target.files[0];
    if (!isImageSizeValid(originalFile.value, props.maxImgSize)) {
      alert(
        `Sua imagem tem ${(originalFile.value.size / 1024 / 1024).toFixed(
          2,
        )}MB. Selecione uma imagem com no máximo ${props.maxImgSize}MB`,
      );
      return;
    }

    toggleModal(modal_id.value);

    if (originalFile.value.type.indexOf("image/") === -1) {
      alert("Por favor selecione uma imagem");
      return;
    }
    if (typeof FileReader === "function") {
      const reader = new FileReader();
      reader.onload = (event) => {
        imgSrc.value = event.target?.result ?? null;
        const cropper = cropperRef.value as any;
        cropper.replace(event.target?.result);
      };
      reader.readAsDataURL(originalFile.value);
    } else {
      alert("Sorry, FileReader API not supported");
    }
  }
};

const cancelUpload = () => {
  uploading.value = false;
  percentage.value = 0;
  cropImg.value = "";
  if (counter.value) {
    clearTimeout(counter.value);
  }
};

const cropClosed = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};
</script>

<style scoped>
input[type="file"] {
  opacity: 0;
  height: 250px;
  width: 92% !important;
  position: absolute;
  z-index: 1;
  cursor: pointer;
}

.form-control-custom {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  opacity: 0.9;
  transition: 0.2s;
  width: 100%;
  height: 250px;
  border: 1px #dce1e8 dashed;
}

.form-control-custom .text {
  position: absolute;
  text-align: center;
}

.btn-white:hover,
.btn-white:focus {
  background-color: #fff;
  border-color: #ed6125 !important;
}
</style>
